import { useCallback, useEffect, useState } from 'react';
import {
  Plus, Zap, Trash2, ToggleLeft, ToggleRight, AlertCircle, ArrowRight, History, Pencil,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { useAuth } from '@/hooks/useAuth';
import { workflowsService, WorkflowRule } from '@/services/workflows.service';
import { templatesService } from '@/services/templates.service';
import { settingsService } from '@/services/settings.service';
import { dealsService } from '@/services/deals.service';
import { whatsappAccountService } from '@/services/whatsapp-account.service';
import { WorkflowRuleEditor, EditorLookups } from '@/components/workflows/WorkflowRuleEditor';
import { WorkflowRunLog } from '@/components/workflows/WorkflowRunLog';
import { ACTION_LABELS, TRIGGER_LABELS } from '@/components/workflows/workflowMeta';

const EMPTY_LOOKUPS: EditorLookups = { templates: [], accountLabels: {}, team: [], stages: [], pipelines: [] };

async function loadLookups(): Promise<EditorLookups> {
  const [templates, team, pipelines, accounts] = await Promise.all([
    templatesService.list({ status: 'APPROVED', limit: 100 }).then(r => r.items).catch(() => []),
    settingsService.listTeam().catch(() => []),
    dealsService.listPipelines().catch(() => []),
    whatsappAccountService.list().catch(() => []),
  ]);
  const stageLists = await Promise.all(pipelines.map(p => dealsService.listStages(p.pipelineUid).catch(() => [])));
  return {
    templates,
    accountLabels: Object.fromEntries(accounts.map(a => [a.accountUid, a.displayPhoneNumber || a.verifiedName])),
    team: team.filter(u => u.status === 'active').map(u => ({ userUid: u.userUid, label: `${u.firstName} ${u.lastName}`.trim() || u.email })),
    pipelines: pipelines.map(p => ({ pipelineUid: p.pipelineUid, name: p.name })),
    stages: pipelines.flatMap((p, index) => stageLists[index].map(s => ({ stageUid: s.stageUid, label: pipelines.length > 1 ? `${p.name} · ${s.name}` : s.name }))),
  };
}

export default function WorkflowsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [rules, setRules] = useState<WorkflowRule[] | null>(null);
  const [editor, setEditor] = useState<{ rule: WorkflowRule | null } | null>(null);
  const [lookups, setLookups] = useState<EditorLookups | null>(null);
  const [del, setDel] = useState<WorkflowRule | null>(null);
  const [runLog, setRunLog] = useState<{ rule?: WorkflowRule } | null>(null);

  const refresh = useCallback(() => workflowsService.list().then(setRules).catch(() => setRules([])), []);
  useEffect(() => { refresh(); }, [refresh]);

  const openEditor = async (rule: WorkflowRule | null) => {
    setEditor({ rule });
    if (!lookups) setLookups(await loadLookups().catch(() => EMPTY_LOOKUPS));
  };

  const toggle = async (r: WorkflowRule) => {
    try {
      await workflowsService.toggle(r.ruleUid, !r.isActive);
      toast.success(r.isActive ? 'Rule turned off — waiting runs that come due while it is off are cancelled' : 'Rule turned on');
      refresh();
    } catch { /* interceptor */ }
  };

  const onDelete = async () => {
    if (!del) return;
    try {
      await workflowsService.remove(del.ruleUid);
      toast.success('Rule deleted');
      setDel(null);
      refresh();
    } catch { /* interceptor */ }
  };

  return (
    <div>
      <Topbar
        title="Workflow automation"
        breadcrumb="Automation"
        description="“When this happens, do that” rules that run on the server. Every run and step is logged; steps never re-run automatically."
        right={(
          <div className="flex items-center gap-2">
            <Button variant="secondary" iconLeft={<History size={14} />} onClick={() => setRunLog({})}>Run log</Button>
            {isAdmin && <Button iconLeft={<Plus size={14} />} onClick={() => openEditor(null)}>New rule</Button>}
          </div>
        )}
      />

      <div className="space-y-4 px-7 py-5">
        {!isAdmin && (
          <p className="rounded-lg border border-border bg-surface-alt/60 px-4 py-2.5 text-xs text-text-muted">
            Rules can message customers and change records, so only company admins can create or change them.
          </p>
        )}
        {!rules && <Card padded><div className="skeleton h-24 rounded-lg" /></Card>}

        {rules && rules.length === 0 && (
          <EmptyState
            icon={<Zap size={28} />}
            title="No workflow rules yet"
            description="Automate routine follow-up — for example, when a Google or Facebook lead arrives, send an approved WhatsApp template, assign an owner and create a call task."
            action={isAdmin ? <Button iconLeft={<Plus size={14} />} onClick={() => openEditor(null)}>Create your first rule</Button> : undefined}
          />
        )}

        {rules && rules.length > 0 && (
          <div className="space-y-3">
            {rules.map((r) => (
              <Card key={r.ruleUid} padded>
                <div className="flex items-start gap-4">
                  <button
                    onClick={() => isAdmin && toggle(r)}
                    disabled={!isAdmin}
                    className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors disabled:cursor-default ${
                      r.isActive ? 'bg-success-soft text-success hover:bg-success/20' : 'bg-surface-alt text-text-muted hover:bg-surface-alt/80'
                    }`}
                    title={isAdmin ? (r.isActive ? 'Turn off' : 'Turn on') : r.isActive ? 'On' : 'Off'}
                  >
                    {r.isActive ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                  </button>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-text-primary">{r.name}</h3>
                      {!r.isActive && <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-semibold text-text-muted">off</span>}
                    </div>
                    {r.description && <p className="mt-0.5 text-xs text-text-muted">{r.description}</p>}

                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-2xs">
                      <span className="rounded-full bg-info-soft px-2 py-0.5 font-medium text-info">{TRIGGER_LABELS[r.trigger] ?? r.trigger}</span>
                      {(r.conditions?.all?.length || r.conditions?.any?.length) ? (
                        <span className="rounded-full bg-surface-alt px-2 py-0.5 text-text-secondary">only if {(r.conditions.all ?? r.conditions.any ?? []).length} condition(s)</span>
                      ) : null}
                      <ArrowRight size={11} className="text-text-muted" />
                      {r.actions.map((a, i) => (
                        <span key={i} className="rounded-full bg-primary-soft px-2 py-0.5 font-medium text-primary">{ACTION_LABELS[a.kind] ?? a.kind}</span>
                      ))}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-3 text-2xs text-text-muted">
                      <span className="tabular-nums">Ran {r.timesFired.toLocaleString()}×</span>
                      {r.lastFiredAt && <span>· last {new Date(r.lastFiredAt).toLocaleString()}</span>}
                      {r.lastErrorAt && (
                        <span className="flex items-center gap-1 text-danger" title={r.lastErrorMessage}>
                          <AlertCircle size={11} /> {r.lastErrorMessage?.slice(0, 80) ?? 'A step failed'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" iconLeft={<History size={13} />} onClick={() => setRunLog({ rule: r })}>Runs</Button>
                    {isAdmin && <Button size="sm" variant="ghost" iconLeft={<Pencil size={13} />} onClick={() => openEditor(r)}>Edit</Button>}
                    {isAdmin && (
                      <button onClick={() => setDel(r)} aria-label="Delete rule"
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-danger-soft hover:text-danger">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {editor && lookups && (
        <WorkflowRuleEditor
          existing={editor.rule}
          lookups={lookups}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); refresh(); }}
        />
      )}
      {runLog && <WorkflowRunLog ruleUid={runLog.rule?.ruleUid} ruleName={runLog.rule?.name} onClose={() => setRunLog(null)} />}

      <ConfirmDialog
        open={!!del}
        title="Delete this workflow rule?"
        message={del ? `"${del.name}" will be removed and its waiting runs cancelled. Steps that already ran are not reversed.` : ''}
        danger
        onConfirm={onDelete}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}
